import type { LogClient, LogDescriptor, LogEntry } from '../../core/log/client';
import { href, navigate } from '../../router';
import { fmtInt, parseIndex } from '../format';
import { summarizeEntry } from '../entrySummary';
import { useAsync } from '../hooks/useAsync';
import { Badge } from './Badge';
import { ErrorBox } from './ErrorBox';
import { Hash } from './Hash';
import { Icon } from './Icon';
import { Spinner } from './Spinner';
import { retryNotices } from '../state/transport';
import { logRoute } from '../nav';
import { useEffect, useState } from 'preact/hooks';

/**
 * One page of entries starting at `start`. Pages are the client's natural batch (a 256-entry tile
 * for tiled logs, the observed cap for RFC 6962 logs), so the DOM never scales with the tree.
 */
export function EntriesTable({ client, treeSize, start, onNavigate }: { client: LogClient; treeSize: number; start: number; onNavigate: (start: number) => void }) {
  const pageSize = client.pageSize;
  const state = useAsync((signal) => client.getEntries(start, pageSize, { treeSize, signal }), [client, start, pageSize, treeSize]);
  const entries = state.value ?? [];
  const got = entries.length;
  const notice = retryNotices.value[client.log.id];
  const prevStart = Math.max(0, start - pageSize);
  const nextStart = start + (got || pageSize);
  const lastStart = Math.max(0, Math.floor((treeSize - 1) / pageSize) * pageSize);

  const pager = (
    <div class="pager">
      <span class="pager-btns">
        <button type="button" class="btn btn-sm" disabled={start === 0} onClick={() => onNavigate(0)} title="First page">⇤</button>
        <button type="button" class="btn btn-sm" disabled={start === 0} onClick={() => onNavigate(prevStart)}><Icon name="chevronLeft" /> Prev</button>
      </span>
      <StartIndex start={start} end={got > 0 ? start + got - 1 : undefined} treeSize={treeSize} onNavigate={onNavigate} loading={state.status === 'loading' && got > 0} />
      <span class="pager-btns">
        <button type="button" class="btn btn-sm" disabled={nextStart >= treeSize} onClick={() => onNavigate(nextStart)}>Next <Icon name="chevronRight" /></button>
        <button type="button" class="btn btn-sm" disabled={start >= lastStart} onClick={() => onNavigate(lastStart)} title="Last page">⇥</button>
      </span>
    </div>
  );

  return (
    <div class="card">
      <div class="card-head">
        <h2><Icon name="list" /> Entries</h2>
        {pager}
      </div>
      {notice && <div class="card-body" style="padding-bottom:0"><div class="notice">Rate limited — retrying in {Math.round(notice.delayMs / 1000)}s…</div></div>}
      {state.status === 'error' && <div class="card-body"><ErrorBox error={state.error} title="Could not fetch entries" /></div>}
      {state.status === 'loading' && got === 0 && <div class="card-body"><Spinner label={`Fetching ${fmtInt(pageSize)} entries…`} /></div>}
      {got > 0 && (
        <div class={`table-wrap ${state.status === 'loading' ? 'dim' : ''}`} style="border:0;border-radius:0">
          <table class="table">
            <thead>
              <tr>
                <th>Index</th>
                <th>Type</th>
                <th>Subject</th>
                <th>Issuer</th>
                <th>Logged</th>
                <th>Expires</th>
                <th>Leaf hash</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <EntryRow key={e.index} entry={e} log={client.log} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {got > 0 && <div class="card-foot"><div class="pager">{pager.props.children}</div></div>}
    </div>
  );
}

/**
 * The pager's status line, with the start index editable in place: "Entries [ 992 ] – 1,023 of N".
 * Type an index and press Enter (or Go) to show the page starting there — the same "jump to index"
 * as before, but where the prev/next buttons are.
 */
function StartIndex({ start, end, treeSize, onNavigate, loading }: { start: number; end: number | undefined; treeSize: number; onNavigate: (start: number) => void; loading: boolean }) {
  // Shown formatted (1,234) until the user edits it; parseIndex() accepts the separators back.
  const [text, setText] = useState(fmtInt(start));
  const [edited, setEdited] = useState(false);
  useEffect(() => { setText(fmtInt(start)); setEdited(false); }, [start]);
  const n = parseIndex(text);
  const valid = n !== undefined && n < treeSize;
  const changed = edited && n !== start;
  const width = `${Math.max(5, String(treeSize).length + 1)}ch`;
  return (
    <form class="status" onSubmit={(e) => { e.preventDefault(); if (valid && n !== start) onNavigate(n); }} title="Type an entry index and press Enter to show the page starting there">
      <span>Entries</span>
      <input
        class={`input input-sm mono ${text && !valid ? 'invalid' : ''}`}
        style={{ width }}
        inputMode="numeric"
        value={text}
        placeholder="index"
        aria-label="Start at entry index"
        onInput={(e) => { setText((e.target as HTMLInputElement).value); setEdited(true); }}
        onKeyDown={(e) => { if (e.key === 'Escape') { setText(fmtInt(start)); setEdited(false); (e.target as HTMLInputElement).blur(); } }}
        onFocus={(e) => (e.target as HTMLInputElement).select()}
      />
      {changed && valid ? (
        <button type="submit" class="btn btn-sm btn-primary" title="Show entries starting at this index">Go <Icon name="arrowRight" /></button>
      ) : (
        <span>{end !== undefined ? <>– <strong>{fmtInt(end)}</strong></> : ''} of {fmtInt(treeSize)}</span>
      )}
      {text && !valid && <span class="tiny muted">below {fmtInt(treeSize)}</span>}
      {loading && <Spinner label="" />}
    </form>
  );
}

function EntryRow({ entry, log }: { entry: LogEntry; log: LogDescriptor }) {
  const s = summarizeEntry(entry);
  const expired = s.notAfter !== undefined && s.notAfter.getTime() < Date.now();
  const to = logRoute(log, `entry/${entry.index}`);
  return (
    <tr class="clickable" onClick={() => navigate(to)}>
      <td class="num"><a href={href(to)} onClick={(e) => e.stopPropagation()}>{fmtInt(entry.index)}</a></td>
      <td>{entry.entryType === 'precert' ? <Badge tone="info">precert</Badge> : <Badge tone="muted">cert</Badge>}</td>
      <td class="cell-name" title={s.parsed?.subject}>
        {s.error ? <span class="muted">{s.name}</span> : s.name}
        {s.parsed && s.parsed.sans.length > 1 && <span class="muted tiny"> +{s.parsed.sans.length - 1}</span>}
      </td>
      <td class="cell-name muted" title={s.parsed?.issuer}>{s.issuer}</td>
      <td class="small nowrap">{new Date(entry.leaf.entry.timestamp).toISOString().replace('T', ' ').slice(0, 19)}</td>
      <td class={`small nowrap ${expired ? 'muted' : ''}`}>{s.notAfter ? s.notAfter.toISOString().slice(0, 10) : '—'}</td>
      <td><Hash value={entry.leafHash} short={6} copy={false} /></td>
      <td class="muted"><Icon name="chevronRight" /></td>
    </tr>
  );
}
